<template>
  <div
    v-if="visible"
    class="dash-events-calendar-wrap h-100 w-100"
    :class="{ 'dash-events-calendar-wrap--compact': compact }"
  >
    <div v-if="!compact" class="dash-events-calendar-section__head">
      <div>
        <p class="dash-events-calendar-section__eyebrow">
          {{ $t("Academic calendar") }}
        </p>
        <h4 class="dash-events-calendar-section__title">
          {{ $t("Events & holidays") }}
        </h4>
      </div>
      <b-spinner v-if="loading" small variant="primary" />
    </div>

    <div
      class="dash-events-calendar-wrap-inner"
      :class="{ 'dash-events-calendar-wrap-inner--compact': compact }"
    >
      <div
        class="app-calendar dash-events-calendar"
        :class="{
          'dash-events-calendar--compact': compact,
          'dash-events-calendar--mini': compact,
        }"
      >
        <div v-if="compact" class="dash-mini-cal__card-head">
          <span class="dash-mini-cal__brand-icon" aria-hidden="true">
            <feather-icon icon="CalendarIcon" size="20" />
          </span>
          <div class="dash-mini-cal__brand-text">
            <h3 class="dash-mini-cal__heading mb-0">
              {{ $t("Events Calendar") }}
            </h3>
            <small class="dash-mini-cal__subtitle">{{
              $t("School events & holidays")
            }}</small>
          </div>
        </div>

        <div v-if="compact" class="dash-mini-cal__toolbar">
          <div class="dash-mini-cal__month-pill">
            <span class="dash-mini-cal__month-dot" aria-hidden="true" />
            <h5 class="dash-mini-cal__title mb-0">{{ viewTitle }}</h5>
          </div>
          <div class="dash-mini-cal__nav">
            <button
              type="button"
              class="dash-mini-cal__nav-btn"
              :aria-label="$t('Previous month')"
              :disabled="loading"
              @click="shiftMonth(-1)"
            >
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </button>
            <button
              type="button"
              class="dash-mini-cal__nav-btn"
              :aria-label="$t('Next month')"
              :disabled="loading"
              @click="shiftMonth(1)"
            >
              <feather-icon icon="ChevronRightIcon" size="18" />
            </button>
          </div>
        </div>

        <!-- Calendar filters (same options as the event-calendar app) -->
        <div v-if="compact" class="dash-mini-cal__filters">
          <button
            type="button"
            class="dash-mini-cal__filter"
            :class="{ 'is-active': filters.all }"
            @click="toggleFilterAll"
          >
            {{ $t("View all") }}
          </button>
          <button
            type="button"
            class="dash-mini-cal__filter"
            :class="{ 'is-active': filters.students }"
            @click="toggleFilter('students')"
          >
            <span class="dash-mini-cal__filter-dot dash-mini-cal__filter-dot--students" />
            {{ $t("Students") }}
          </button>
          <button
            type="button"
            class="dash-mini-cal__filter"
            :class="{ 'is-active': filters.staff }"
            @click="toggleFilter('staff')"
          >
            <span class="dash-mini-cal__filter-dot dash-mini-cal__filter-dot--staff" />
            {{ $t("Staff") }}
          </button>
        </div>

        <div class="dash-events-calendar__frame dash-events-calendar__glass-inset">
          <div
            v-if="compact && showLoader"
            class="dash-ev-cal-loader"
            aria-busy="true"
            :aria-label="$t('Loading calendar')"
          >
            <div class="dash-ev-cal-loader__inner">
              <b-spinner variant="primary" />
              <span>{{ $t("Loading events…") }}</span>
            </div>
          </div>

          <div class="dash-events-calendar__fc-wrap">
            <full-calendar
              ref="refCalendar"
              :options="calendarOptions"
              class="full-calendar dash-events-calendar__fc"
            >
              <template v-if="!compact" v-slot:eventContent="arg">
                <span
                  class="dash-ev-dot"
                  :title="arg.event.title"
                  :class="{
                    'dash-ev-dot--holiday':
                      arg.event.extendedProps.eventType === 'holiday',
                  }"
                />
                <span class="dash-ev-label">{{
                  truncate(arg.event.title, 28)
                }}</span>
              </template>
              <template v-else v-slot:eventContent="arg">
                <span
                  class="dash-mini-ev-dot"
                  :class="miniDotClass(arg.event)"
                  :title="arg.event.title"
                />
              </template>
            </full-calendar>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-if="compact"
      v-model="dayModalShow"
      modal-class="dash-day-events-modal"
      content-class="dash-day-events-modal__content"
      hide-footer
      centered
      size="sm"
      :title="dayModalTitle"
    >
      <div v-if="!dayModalEvents.length" class="dash-day-events-modal__empty">
        {{ $t("No events on this day") }}
      </div>
      <ul v-else class="dash-day-events-modal__list">
        <li
          v-for="ev in dayModalEvents"
          :key="ev.id"
          class="dash-day-events-modal__item"
        >
          <span class="dash-mini-dot" :class="miniDotClass(ev)" />
          <div class="dash-day-events-modal__text">
            <span class="dash-day-events-modal__name">{{ ev.title }}</span>
            <small
              v-if="ev.extendedProps && ev.extendedProps.location"
              class="text-muted d-block"
            >
              {{ ev.extendedProps.location }}
            </small>
          </div>
        </li>
      </ul>
    </b-modal>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { BSpinner, BModal } from "bootstrap-vue";
import { mapActions } from "vuex";
import {
  normalizeEventsCalendarLoadResponse,
  yearFromCalendarInfo,
} from "./utils/dashboardEventsCalendar";

export default {
  name: "DashboardEventsCalendar",
  components: { FullCalendar, BSpinner, BModal },
  props: {
    visible: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      eventsReady: false,
      viewTitle: "",
      dayModalShow: false,
      dayModalTitle: "",
      dayModalEvents: [],
      calendarOptions: null,
      // Calendar filters — mirrors the event-calendar sidebar (Staff / Students).
      filters: { all: true, students: true, staff: true },
    };
  },
  computed: {
    showLoader() {
      return this.loading || !this.eventsReady;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.applyFilters();
      },
    },
  },
  created() {
    this.calendarOptions = this.buildCalendarOptions();
  },
  mounted() {
    this.refreshCalendarSize();
    this._onWinResize = () => this.refreshCalendarSize();
    window.addEventListener("resize", this._onWinResize);
    if (typeof ResizeObserver !== "undefined") {
      this._calResizeObs = new ResizeObserver(() => this.refreshCalendarSize());
      this.$nextTick(() => {
        const el = this.$el;
        if (el) this._calResizeObs.observe(el);
      });
    }
    [80, 250, 600].forEach((ms) => {
      this._sizeTimers = this._sizeTimers || [];
      this._sizeTimers.push(setTimeout(() => this.refreshCalendarSize(), ms));
    });
  },
  beforeDestroy() {
    if (this._onWinResize) {
      window.removeEventListener("resize", this._onWinResize);
    }
    if (this._calResizeObs) {
      this._calResizeObs.disconnect();
    }
    if (this._calSizeRaf) {
      cancelAnimationFrame(this._calSizeRaf);
      this._calSizeRaf = 0;
    }
    if (this._sizeTimers) {
      this._sizeTimers.forEach((t) => clearTimeout(t));
      this._sizeTimers = [];
    }
  },
  methods: {
    ...mapActions({ postObj: "postObj" }),

    buildCalendarOptions() {
      const rtl = this.$store.state.appConfig?.isRTL ? "rtl" : "ltr";
      const base = {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        events: this.fetchEvents,
        editable: false,
        selectable: false,
        navLinks: false,
        direction: rtl,
        fixedWeekCount: true,
        displayEventTime: false,
      };

      if (this.compact) {
        return {
          ...base,
          headerToolbar: false,
          dayMaxEvents: 4,
          dayMaxEventRows: 2,
          height: "auto",
          aspectRatio: 0.88,
          expandRows: true,
          showNonCurrentDates: true,
          eventDisplay: "block",
          eventClassNames: ["dash-mini-fc-event"],
          dayHeaderClassNames: () => ["dash-mini-cal__dow"],
          dayCellClassNames: () => ["dash-mini-cal__cell"],
          views: {
            dayGridMonth: {
              columnHeaderFormat: { weekday: "short" },
            },
          },
          dateClick: this.handleDateClick,
          eventClick: this.handleEventClick,
          datesSet: this.handleDatesSet,
          eventDidMount: this.stripEventChrome,
        };
      }

      return {
        ...base,
        headerToolbar: {
          start: "prev,next title",
          end: "",
        },
        dayMaxEvents: 2,
        dayMaxEventRows: 2,
        height: 340,
        eventDisplay: "block",
        datesSet: this.handleDatesSet,
      };
    },

    refreshCalendarSize() {
      // Coalesce bursts (ResizeObserver + datesSet + resize) into one rAF so we
      // never queue several FullCalendar layout passes for a single frame.
      if (this._calSizeRaf) return;
      this._calSizeRaf = requestAnimationFrame(() => {
        this._calSizeRaf = 0;
        const api = this.calendarApi();
        if (api && typeof api.updateSize === "function") {
          api.updateSize();
        }
      });
    },

    calendarApi() {
      const ref = this.$refs.refCalendar;
      return ref && typeof ref.getApi === "function" ? ref.getApi() : null;
    },

    shiftMonth(delta) {
      const api = this.calendarApi();
      if (!api || this.loading) return;
      if (delta < 0) api.prev();
      else api.next();
      this.refreshCalendarSize();
    },

    /** True when the event matches the active Staff / Students filters. */
    matchesFilters(event) {
      const { all, students, staff } = this.filters;
      if (all) return true;
      if (!students && !staff) return false;
      const props = event.extendedProps || {};
      // Holidays and untagged entries are not Staff/Students specific — hide
      // them as soon as the user narrows the view to an audience.
      if (props.eventType === "holiday") return false;
      const calendars = props.calendars || [];
      if (!calendars.length) return false;
      return calendars.some((cal) => {
        const key = String(cal).toLowerCase();
        if (key === "students" || key === "parents") return students;
        if (key === "staff") return staff;
        return false;
      });
    },

    applyFilters() {
      const api = this.calendarApi();
      if (!api) return;
      api.getEvents().forEach((ev) => {
        ev.setProp("display", this.matchesFilters(ev) ? "auto" : "none");
      });
      this.refreshCalendarSize();
    },

    toggleFilterAll() {
      const next = !this.filters.all;
      this.filters = { all: next, students: next, staff: next };
    },

    toggleFilter(key) {
      const next = { ...this.filters };
      next[key] = !next[key];
      // "View all" is on only while both audiences are on.
      next.all = next.students && next.staff;
      this.filters = next;
    },

    handleDatesSet(arg) {
      this.viewTitle = arg.view.title || "";
      this.refreshCalendarSize();
    },

    stripEventChrome(info) {
      if (!this.compact || !info.el) return;
      info.el.style.background = "transparent";
      info.el.style.border = "none";
      info.el.style.boxShadow = "none";
      info.el.style.maxWidth = "100%";
      info.el.style.overflow = "hidden";
      const main = info.el.querySelector(".fc-event-main");
      if (main) {
        main.style.background = "transparent";
        main.style.border = "none";
        main.style.padding = "0";
        main.style.overflow = "hidden";
      }
      const dot = info.el.querySelector(".fc-daygrid-event-dot");
      if (dot) dot.style.display = "none";
    },

    miniDotClass(event) {
      const props = event.extendedProps || {};
      if (props.eventType === "holiday") return "dash-mini-dot--holiday";
      const idx = Number(props.colorIndex) || 0;
      return `dash-mini-dot--c${idx % 4}`;
    },

    eventsOnDate(dateStr) {
      const api = this.calendarApi();
      if (!api || !dateStr) return [];
      return api.getEvents().filter((ev) => {
        if (!ev.start) return false;
        const y = ev.start.getFullYear();
        const m = String(ev.start.getMonth() + 1).padStart(2, "0");
        const d = String(ev.start.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}` === dateStr;
      });
    },

    openDayModal(dateStr) {
      const events = this.eventsOnDate(dateStr);
      this.dayModalTitle = this.formatDayLabel(dateStr);
      this.dayModalEvents = events.map((ev) => ({
        id: ev.id,
        title: ev.title,
        extendedProps: ev.extendedProps,
      }));
      this.dayModalShow = true;
    },

    handleDateClick(info) {
      if (!this.compact) return;
      this.openDayModal(info.dateStr);
    },

    handleEventClick(info) {
      if (!this.compact) return;
      info.jsEvent.preventDefault();
      const start = info.event.start;
      if (!start) return;
      const y = start.getFullYear();
      const m = String(start.getMonth() + 1).padStart(2, "0");
      const d = String(start.getDate()).padStart(2, "0");
      this.openDayModal(`${y}-${m}-${d}`);
    },

    formatDayLabel(dateStr) {
      const d = new Date(`${dateStr}T12:00:00`);
      if (Number.isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    },

    truncate(text, max = 28) {
      const t = String(text || "").trim();
      if (t.length <= max) return t;
      return `${t.slice(0, Math.max(0, max - 1))}…`;
    },

    fetchEvents(info, successCallback, failureCallback) {
      const year = yearFromCalendarInfo(info);
      this.loading = true;

      this.postObj({
        url:
          this.$store.state.domain +
          "EventsCalendar/Load?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: {
          year,
          months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        },
        token: this.$store.state.userData.token,
        allResponse: true,
        ignoreMessage: true,
      })
        .then((response) => {
          successCallback(normalizeEventsCalendarLoadResponse(response));
          this.$nextTick(() => this.applyFilters());
        })
        .catch(() => {
          if (typeof failureCallback === "function") failureCallback();
          else successCallback([]);
        })
        .finally(() => {
          this.loading = false;
          this.eventsReady = true;
          this.refreshCalendarSize();
        });
    },
  },
};
</script>

<style lang="scss">
.dash-day-events-modal__content {
  border-radius: 18px !important;
  border: 1px solid rgba(115, 103, 240, 0.12) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(12px);
  overflow: hidden;
}

@import "@core/scss/vue/apps/calendar.scss";

.dash-events-calendar-wrap {
  isolation: isolate;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.dash-events-calendar-wrap--compact {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  flex: 1 1 auto;
  overflow: visible;
}

.dash-events-calendar-wrap-inner {
  position: relative;
  width: 100%;
  overflow: hidden;

  &--compact {
    flex: 1 1 auto;
    min-height: 520px;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
}

.dash-events-calendar {
  border-radius: 22px !important;
  border: 1px solid rgba(115, 103, 240, 0.12) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.88) inset,
    0 14px 36px rgba(115, 103, 240, 0.08) !important;
  background: rgba(255, 255, 255, 0.62) !important;
  backdrop-filter: blur(16px) saturate(1.12);
  -webkit-backdrop-filter: blur(16px) saturate(1.12);

  &--mini {
    padding: 1rem 1rem 0.95rem;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    min-height: 520px;
    overflow: visible;
  }
}

.dash-mini-cal__card-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(115, 103, 240, 0.08);
  flex-shrink: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  margin-left: -0.15rem;
  margin-right: -0.15rem;
  padding-left: 0.15rem;
  padding-right: 0.15rem;
}

.dash-mini-cal__brand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.32);
  flex-shrink: 0;
}

.dash-mini-cal__heading {
  font-size: 1.08rem;
  font-weight: 800;
  color: #2f2b3d;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.dash-mini-cal__subtitle {
  display: block;
  font-size: 0.78rem;
  color: #6e6b7b;
  margin-top: 0.12rem;
}

.dash-events-calendar__glass-inset {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0.65rem 0.55rem 0.55rem;
  border-radius: 18px;
  background: linear-gradient(
    165deg,
    rgba(255, 255, 255, 0.52) 0%,
    rgba(247, 245, 255, 0.38) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    0 4px 16px rgba(115, 103, 240, 0.05);
}

.dash-events-calendar__fc-wrap {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  overflow: visible;
}

.dash-events-calendar__fc.full-calendar {
  width: 100% !important;
  max-width: 100% !important;

  .fc {
    width: 100% !important;
    max-width: 100% !important;
  }

  .fc-view-harness {
    width: 100% !important;
  }
}

.dash-ev-cal-loader {
  position: absolute;
  inset: 0;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  pointer-events: none;
  border-radius: 16px;

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.55rem;
    padding: 1rem 1.35rem;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(115, 103, 240, 0.12);
    box-shadow: 0 10px 28px rgba(115, 103, 240, 0.12);

    span {
      font-size: 0.78rem;
      font-weight: 600;
      color: #6e6b7b;
    }
  }
}

.dash-mini-cal__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
  flex-shrink: 0;
}

.dash-mini-cal__month-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.75rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(115, 103, 240, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  min-width: 0;
}

.dash-mini-cal__month-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7367f0, #28c76f);
  flex-shrink: 0;
}

.dash-mini-cal__title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #2f2b3d;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-mini-cal__nav {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.45);
  border: 1px solid rgba(115, 103, 240, 0.08);
}

.dash-mini-cal__nav-btn {
  width: 2.1rem;
  height: 2.1rem;
  border: none;
  border-radius: 11px;
  background: rgba(115, 103, 240, 0.08);
  color: #7367f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.12s ease;

  &:hover:not(:disabled) {
    background: rgba(115, 103, 240, 0.16);
    color: #6254e8;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

/* Calendar audience filters */
.dash-mini-cal__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.65rem;
  flex-shrink: 0;
  padding: 0.3rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(115, 103, 240, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.dash-mini-cal__filter {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.72rem;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  color: #a8aaae;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.4;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    color: #6e6b7b;
    background: rgba(255, 255, 255, 0.5);
  }

  &.is-active {
    background: rgba(255, 255, 255, 0.88);
    border-color: rgba(115, 103, 240, 0.14);
    color: #7367f0;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.1);
  }
}

.dash-mini-cal__filter-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex-shrink: 0;

  &--students {
    background: #3b82f6;
  }

  &--staff {
    background: #ef4444;
  }
}

.dash-events-calendar__frame {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  overflow: visible;
}

/* ── Mini calendar: override global .app-calendar / FullCalendar theme ── */
.dash-events-calendar.app-calendar.dash-events-calendar--mini {
  .full-calendar .fc-view-harness {
    margin: 0 !important;
    overflow: visible !important;
  }

  .full-calendar .fc-scroller-harness-liquid,
  .full-calendar .fc-scroller-harness,
  .full-calendar .fc-scroller-liquid-absolute,
  .full-calendar .fc-scroller {
    overflow: visible !important;
  }

  .full-calendar .fc-theme-standard .fc-scrollgrid,
  .full-calendar .fc-theme-standard .fc-scrollgrid-sync-table,
  .full-calendar .fc-theme-standard td,
  .full-calendar .fc-theme-standard th {
    border: none !important;
  }

  .full-calendar .fc .fc-day-today {
    background: transparent !important;
  }

  .full-calendar .fc-col-header {
    margin-bottom: 0.35rem;
  }

  .full-calendar .fc .fc-col-header-cell.dash-mini-cal__dow {
    border: none !important;
    padding: 0 3px 0.25rem;
    vertical-align: middle;
  }

  .full-calendar .fc .fc-col-header-cell-cushion {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 1.75rem;
    padding: 0.25rem 0.15rem;
    border-radius: 10px;
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
    border: 1px solid rgba(115, 103, 240, 0.1);
  }

  .full-calendar .fc-daygrid-body {
    width: 100% !important;
  }

  .full-calendar .fc .fc-daygrid-day.dash-mini-cal__cell {
    border: none !important;
    padding: 3px;
    vertical-align: top;
  }

  .full-calendar .fc .fc-daygrid-day-frame {
    display: flex;
    flex-direction: column;
    min-height: 54px;
    height: 100%;
    padding: 0.35rem 0.3rem 0.28rem;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.55);
    border: 1px solid rgba(115, 103, 240, 0.07);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.92),
      0 2px 8px rgba(115, 103, 240, 0.04);
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.15s ease, border-color 0.15s ease,
      background 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.82);
      border-color: rgba(115, 103, 240, 0.16);
      box-shadow: 0 6px 16px rgba(115, 103, 240, 0.1);
    }
  }

  .full-calendar .fc .fc-day-other .fc-daygrid-day-frame {
    background: rgba(255, 255, 255, 0.22);
    border-color: rgba(115, 103, 240, 0.04);
    box-shadow: none;
  }

  .full-calendar .fc .fc-day-today .fc-daygrid-day-frame {
    background: linear-gradient(
      145deg,
      rgba(115, 103, 240, 0.14) 0%,
      rgba(255, 255, 255, 0.72) 100%
    );
    border-color: rgba(115, 103, 240, 0.28);
    box-shadow:
      inset 0 0 0 1px rgba(115, 103, 240, 0.12),
      0 6px 18px rgba(115, 103, 240, 0.12);
  }

  .full-calendar .fc .fc-daygrid-day-top {
    flex-direction: row;
    justify-content: flex-start;
    margin-bottom: 0.15rem;
  }

  .full-calendar .fc .fc-daygrid-day-number {
    float: none !important;
    position: static !important;
    width: 1.7rem;
    height: 1.7rem;
    margin: 0 !important;
    padding: 0 !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.74rem;
    font-weight: 700;
    color: #5e5873;
    border-radius: 10px;
  }

  .full-calendar .fc .fc-day-today .fc-daygrid-day-number {
    background: linear-gradient(135deg, #7367f0 0%, #6254e8 100%);
    color: #fff;
    font-weight: 800;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  }

  .full-calendar .fc .fc-day-other .fc-daygrid-day-number {
    color: #b9b9c3;
    font-weight: 600;
  }

  .full-calendar .fc .fc-daygrid-day-events {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 3px;
    margin: 0 !important;
    padding: 0 0.05rem;
    min-height: 0;
  }

  .full-calendar .fc .fc-daygrid-event-harness {
    margin: 0 !important;
    max-width: none;
  }

  .full-calendar .fc .fc-daygrid-event,
  .full-calendar .fc .fc-h-event,
  .full-calendar .fc .dash-mini-fc-event {
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .full-calendar .fc .fc-event-main {
    padding: 0 !important;
    line-height: 1;
  }

  .full-calendar .fc .fc-daygrid-more-link {
    display: none !important;
  }
}

.dash-mini-ev-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);

  &.dash-mini-dot--c0 {
    background: #3b82f6;
  }
  &.dash-mini-dot--c1 {
    background: #ef4444;
  }
  &.dash-mini-dot--c2 {
    background: #8b5cf6;
  }
  &.dash-mini-dot--c3 {
    background: #22c55e;
  }
  &.dash-mini-dot--holiday {
    background: #22c55e;
  }
}

.dash-events-calendar--mini {
  .dash-events-calendar__fc.full-calendar {
    padding: 0 !important;
  }

  .full-calendar .fc .fc-view-harness {
    min-height: 0 !important;
  }

  .full-calendar .fc-scrollgrid,
  .full-calendar .fc-scrollgrid-sync-table,
  .full-calendar .fc-scrollgrid table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: fixed !important;
  }

  .full-calendar .fc .fc-col-header-cell,
  .full-calendar .fc .fc-daygrid-day {
    width: 14.2857% !important;
  }
}

.dash-mini-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;

  &--c0 {
    background: #3b82f6;
  }
  &--c1 {
    background: #ef4444;
  }
  &--c2 {
    background: #8b5cf6;
  }
  &--c3 {
    background: #22c55e;
  }
  &--holiday {
    background: #22c55e;
  }
}

.dash-day-events-modal__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dash-day-events-modal__item {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.55rem 0;

  + .dash-day-events-modal__item {
    border-top: 1px solid #f1f5f9;
  }
}

.dash-day-events-modal__name {
  font-weight: 700;
  font-size: 0.88rem;
  color: #1e293b;
}

.dash-day-events-modal__empty {
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
  padding: 0.5rem 0;
}

.dash-events-calendar-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.dash-events-calendar-section__eyebrow {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.dash-events-calendar-section__title {
  margin: 0.15rem 0 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
}
</style>
